/**
 * Tiny WebSocket subscription hook with auto-reconnect.
 *
 * Usage:
 *   useWebSocket('/ws/tasks/abc', (msg) => { ... });
 *
 * Pauses while the tab is hidden (visibilitychange). Reconnects with
 * exponential backoff up to 30s.
 */

import { useEffect, useRef } from 'react';

type WsMessage = { kind: string; [k: string]: unknown };

export function useWebSocket(
  url: string | null,
  onMessage: (msg: WsMessage) => void,
): void {
  // Stash the latest handler in a ref so we don't re-subscribe on every render.
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!url) return;
    let socket: WebSocket | null = null;
    let backoffMs = 500;
    let reconnectTimer: number | null = null;
    let stopped = false;

    const wsUrl = url.startsWith('ws')
      ? url
      : (location.protocol === 'https:' ? 'wss:' : 'ws:') +
        '//' +
        location.host +
        url;

    const connect = () => {
      if (stopped) return;
      socket = new WebSocket(wsUrl);
      socket.addEventListener('open', () => {
        backoffMs = 500;
      });
      socket.addEventListener('message', (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as WsMessage;
          handlerRef.current(msg);
        } catch {
          // ignore malformed
        }
      });
      socket.addEventListener('close', () => {
        if (stopped) return;
        reconnectTimer = window.setTimeout(connect, backoffMs);
        backoffMs = Math.min(backoffMs * 2, 30_000);
      });
      socket.addEventListener('error', () => {
        socket?.close();
      });
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [url]);
}
